// src/pages/messages/SmsTemplatesPage.tsx

import {
  AlertCircle,
  CheckCircle2,
  Code2,
  FileText,
  Loader2,
  MessageCircle,
  RefreshCw,
  Save,
  Search,
  Settings2,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { fetchApp } from "../../services/apiService";

interface SmsTemplate {
  id: number;
  code: string;
  name: string;
  description?: string;
  content: string;
  is_active: boolean;
}

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const textareaCls =
  "mt-3 w-full resize-none rounded-2xl border border-slate-300 bg-slate-50 px-3.5 py-3 text-[13px] font-medium leading-relaxed text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const normalizeList = <T,>(res: any): T[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.results)) return res.results;
  return [];
};

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const TemplateSkeleton: React.FC = () => (
  <div className="animate-pulse rounded-3xl border border-slate-200 bg-slate-50/95 p-4 shadow-sm sm:p-5">
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-2">
        <div className="h-4 w-48 rounded-lg bg-slate-200" />
        <div className="h-3 w-64 rounded-lg bg-slate-200" />
      </div>

      <div className="h-7 w-20 rounded-full bg-slate-200" />
    </div>

    <div className="mt-4 h-28 rounded-2xl bg-slate-200" />

    <div className="mt-3 flex justify-end">
      <div className="h-10 w-24 rounded-2xl bg-slate-200" />
    </div>
  </div>
);

const SmsTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [placeholders, setPlaceholders] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");

  const loadTemplates = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (templates.length === 0) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      const res = await fetchApp("/sms-templates/");
      setTemplates(normalizeList<SmsTemplate>(res));

      const ph = await fetchApp("/sms-templates/placeholders");
      setPlaceholders(ph?.placeholders || {});
    } catch (err: any) {
      setError(err?.message || "Failed to load templates");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (id: number, value: string) => {
    setTemplates((prev) =>
      prev.map((tpl) => (tpl.id === id ? { ...tpl, content: value } : tpl)),
    );
  };

  const handleSave = async (tpl: SmsTemplate) => {
    try {
      setSavingId(tpl.id);
      setError(null);
      setSuccess(null);

      await fetchApp(`/sms-templates/${tpl.id}`, {
        method: "PUT",
        body: JSON.stringify({ content: tpl.content }),
      });

      setSuccess(`${tpl.name} saved successfully.`);
    } catch (err: any) {
      setError(err?.message || "Failed to save template");
    } finally {
      setSavingId(null);
    }
  };

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return templates;

    return templates.filter((tpl) =>
      [
        tpl.name,
        tpl.code,
        tpl.description || "",
        tpl.content,
        tpl.is_active ? "active" : "inactive",
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [templates, search]);

  const stats = useMemo(() => {
    const active = templates.filter((tpl) => tpl.is_active).length;
    const inactive = templates.length - active;
    const placeholderCount = Object.keys(placeholders).length;

    return {
      total: templates.length,
      active,
      inactive,
      placeholderCount,
    };
  }, [templates, placeholders]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div className="mx-auto max-w-[1600px] space-y-5">
          <div className="flex min-h-[45vh] flex-col items-center justify-center gap-3 rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
            <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
              <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
            </div>

            <p className="text-[13px] font-bold text-slate-500">
              Loading SMS templates…
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <TemplateSkeleton key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Messaging Configuration
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  SMS Templates
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Manage reusable SMS message templates and dynamic placeholders
                  used when sending customer notifications.
                </p>
              </div>

              <button
                type="button"
                onClick={loadTemplates}
                disabled={refreshing || savingId !== null}
                className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {refreshing && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing SMS templates…
          </div>
        )}

        {(error || success) && (
          <div
            className={`flex items-start justify-between gap-3 rounded-3xl border px-4 py-3.5 text-[13px] font-bold shadow-sm ${
              error
                ? "border-rose-200 bg-rose-50 text-rose-700 shadow-rose-200/40"
                : "border-emerald-200 bg-emerald-50 text-emerald-800 shadow-emerald-200/40"
            }`}
          >
            <div className="flex items-start gap-3">
              {error ? (
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              )}

              <p>{error || success}</p>
            </div>

            <button
              type="button"
              onClick={() => {
                setError(null);
                setSuccess(null);
              }}
              className={`rounded-xl p-1.5 transition-colors ${
                error
                  ? "text-rose-600 hover:bg-rose-100"
                  : "text-emerald-700 hover:bg-emerald-100"
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:gap-4">
          <StatCard
            label="Total Templates"
            value={stats.total}
            sub="All saved templates"
            icon={<FileText className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Active"
            value={stats.active}
            sub="Available for sending"
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Inactive"
            value={stats.inactive}
            sub="Currently disabled"
            icon={<Settings2 className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Placeholders"
            value={stats.placeholderCount}
            sub="Dynamic variables"
            icon={<Code2 className="h-5 w-5" />}
            tone="sky"
          />
        </div>

        {/* Placeholders panel */}
        <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-950 shadow-xl shadow-slate-400/25">
          <div className="flex flex-col gap-3 border-b border-slate-800 bg-slate-900 px-4 py-4 sm:px-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-300">
                <Code2 className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-50">
                  Dynamic Placeholders
                </p>

                <p className="mt-0.5 text-[12px] font-medium leading-relaxed text-slate-400">
                  Placeholders like{" "}
                  <code className="rounded-lg bg-slate-800 px-1.5 py-0.5 text-emerald-300">
                    :first_name
                  </code>{" "}
                  are automatically replaced with customer details when messages
                  are sent.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-2 sm:p-5 xl:grid-cols-3">
            {Object.entries(placeholders).length > 0 ? (
              Object.entries(placeholders).map(([key, desc]) => (
                <div
                  key={key}
                  className="rounded-2xl border border-slate-800 bg-slate-900/80 px-3 py-3"
                >
                  <code className="text-[12px] font-black text-emerald-300">
                    {key}
                  </code>

                  <p className="mt-1 text-[11.5px] font-medium leading-relaxed text-slate-400">
                    {desc}
                  </p>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-3 py-3 text-[12px] font-medium text-slate-400">
                No placeholders found.
              </div>
            )}
          </div>
        </div>

        {/* Templates panel */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <MessageCircle className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-900">
                  Template Editor
                </p>

                <p className="text-[11.5px] font-medium text-slate-500">
                  {filteredTemplates.length} of {templates.length} templates
                </p>
              </div>
            </div>

            <div className="relative w-full lg:w-96">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search template name, code or content…"
                className={`${inputCls} pl-10`}
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 p-4 sm:p-5 xl:grid-cols-2">
            {filteredTemplates.length > 0 ? (
              filteredTemplates.map((tpl) => (
                <div
                  key={tpl.id}
                  className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-100/70 shadow-sm"
                >
                  <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-4 py-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="truncate text-[14px] font-black text-slate-900">
                          {tpl.name}
                        </h2>

                        <span className="rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 font-mono text-[10.5px] font-black uppercase text-slate-600">
                          {tpl.code}
                        </span>

                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-black ${
                            tpl.is_active
                              ? "border-emerald-200 bg-emerald-100 text-emerald-800"
                              : "border-slate-300 bg-slate-200 text-slate-600"
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              tpl.is_active ? "bg-emerald-500" : "bg-slate-500"
                            }`}
                          />
                          {tpl.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>

                      {tpl.description && (
                        <p className="mt-1 text-[12px] font-medium leading-relaxed text-slate-500">
                          {tpl.description}
                        </p>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => handleSave(tpl)}
                      disabled={savingId === tpl.id}
                      className={`${btnBase} h-10 shrink-0 bg-gradient-to-r from-emerald-500 to-teal-500 px-4 text-white shadow-md shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                    >
                      {savingId === tpl.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      {savingId === tpl.id ? "Saving…" : "Save"}
                    </button>
                  </div>

                  <div className="p-4">
                    <textarea
                      className={textareaCls}
                      rows={5}
                      value={tpl.content}
                      onChange={(e) => handleChange(tpl.id, e.target.value)}
                      placeholder="Write template content here..."
                    />

                    <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Preview
                      </p>

                      <p className="mt-1 whitespace-pre-wrap text-[12.5px] font-medium leading-relaxed text-slate-700">
                        {tpl.content || "No content added yet."}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <Search className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No templates found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  Try clearing your search or refreshing the template list.
                </p>

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[12px] font-medium text-slate-500">
              Showing{" "}
              <span className="font-black text-slate-800">
                {filteredTemplates.length}
              </span>{" "}
              of{" "}
              <span className="font-black text-slate-800">
                {templates.length}
              </span>{" "}
              templates
            </p>

            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="text-left text-[12px] font-black text-emerald-700 hover:text-emerald-800 sm:text-right"
              >
                Clear search
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsTemplatesPage;
